import React, { useState, useEffect, useRef }  from 'react';
import CardDeck from 'react-bootstrap/CardDeck';
import Card from 'react-bootstrap/Card';
import { Link, useParams, Redirect } from 'react-router-dom';
import { formatDate, formatTime, formatDuration } from './utils/dateUtils';

function ServiceDetailsPage({loadingServiceData, loadedServiceData, serviceData, getServiceData, user, interpretations, getInterpretations, loadingInterpretations, loadedInterpretations}) {
    const { id } = useParams();
    const service = serviceData.find(s => s.ServiceID === id);
    const role = user.role.type;
    const isAdmin = role === 'ADMIN';
    const playlistID = service ? service.PlaylistID : null;
    const [currentTime, setCurrentTime] = useState('');    
    const [videoStartTime, setVideoStartTime] = useState('');
    const [videoEndTime, setVideoEndTime] = useState('');
    const [player, setPlayer] = useState(null);
    const [playerReady, setPlayerReady] = useState(false);
    const [playerError, setPlayerError] = useState('');
    const [currentVideoId, setCurrentVideoID] = useState();
    const [videoInterpretation, setVideoInterpretation] = useState({});
    const youTubeApiInitialised = useRef(false);
    let currentPlayer;
    let intervalHandle;

    useEffect(
        () => {
            if(!serviceData.length && !loadingServiceData && !loadedServiceData) {
                getServiceData();
            }
            if(!interpretations.length && !loadingInterpretations && !loadedInterpretations) {
                getInterpretations();
            }
            return function cleanup() {
                if(intervalHandle) {
                    clearInterval(intervalHandle);
                }
            };
        }, []
      );

      useEffect(() => {
        if(interpretations?.length) {
            setVideoInterpretation(interpretations.find(i => i.VideoID === currentVideoId));
        }
      }, [currentVideoId])

      //wait for the service data before creating the player, as the container isn't rendered until then
      useEffect(() => {
        if(isAdmin && playlistID && !youTubeApiInitialised.current) {
            youTubeApiInitialised.current = true;
            initialiseIFrameAPI();
        }
      }, [isAdmin, playlistID])

    const getProgramme = () => {
        let startTime = new Date(service.StartTime);
        const programme = [];
        Object.keys(service.VideoDurations).forEach(videoID => {
            const videoInfo = service.VideoDurations[videoID];
            const endTime = new Date(startTime.getTime() + (videoInfo.Duration * 1000));
            programme.push({
                VideoID: videoID,
                Title: videoInfo.Title,
                StartTime: formatTime(startTime),
                EndTime: formatTime(endTime)
            });
            startTime = endTime;
        });
        return programme;
    }

    const getStartDateText = (service) => {
        const startDate = new Date(service.StartTime);
        return formatDate(startDate);
    }

    const getStartTime = (service) => {
        const startDate = new Date(service.StartTime);
        const startTime = new Date(startDate.getTime() + service.IntroLengthMins * 60000);
        return startTime;
    }

    const getStartTimeText = (service) => {  
        return formatTime(getStartTime(service));
    }

    const getEndTimeText = (service) => {
        const startTime = new Date(service.StartTime);
        const videoIDs = Object.keys(service.VideoDurations);
        const videoDurationList = videoIDs.map(videoID => service.VideoDurations[videoID].Duration);
        const serviceDurationSecs = videoDurationList.reduce((accumulator, currentValue) => {
            return accumulator + currentValue;
        });
        const endTime = new Date(startTime.getTime() + serviceDurationSecs * 1000);
        return formatTime(endTime);
    }

    const initialiseIFrameAPI = () => {      
        if (!window.YT) { // If not, load the script asynchronously
            const tag = document.createElement('script');
            tag.id = 'iframe-script';
            tag.src = '//www.youtube.com/iframe_api';
            window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;
            const firstScriptTag = document.getElementsByTagName('script')[0];
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        } 
        else if(!player && window.YT.Player) { // If script is already there, load the video directly
            onYouTubeIframeAPIReady();
        }
    }                
    
    const onYouTubeIframeAPIReady = () => {
        const youTubePlayer = new window.YT.Player('contentFrame', {
            playerVars: {
                autoplay: 0
            },
            events: {
                'onReady': onPlayerReady,
                'onStateChange': onPlayerStateChange,
                'onError': onPlayerError
            }
        });
        setPlayer(youTubePlayer);
        currentPlayer = youTubePlayer;
    }
    
    const onPlayerReady = (event) => {
        setPlayerReady(true);
        setCurrentTime('00:00:00');
        const myPlayer = player || currentPlayer;
        if(myPlayer && myPlayer.getPlayerState() === window.YT.PlayerState.PLAYING) {
            setCurrentTimeInterval();
        }
    }
    
    const playerErrorText = (code) => {
        switch(code) {
            case 2: return 'invalid parameter (bad video or playlist ID)';
            case 5: return 'the HTML5 player cannot play this content';
            case 100: return 'the video was not found (removed or made private)';
            case 101:
            case 150: return 'the owner does not allow this video to be played in embedded players';
            case 153: return 'the request did not include an HTTP Referer header';
            default: return 'unknown error';
        }
    }

    const onPlayerError = (event) => {
        const code = event.data;
        console.error(`YouTube player error ${code}: ${playerErrorText(code)}`);
        setPlayerError(`${code} - ${playerErrorText(code)}`);
    }

    const onPlayerStateChange = (event) => {
        const playerStatus = event.data;
        if(playerStatus === window.YT.PlayerState.PLAYING && !intervalHandle) {
            setCurrentTimeInterval();
        }
        else if(intervalHandle) {
            clearInterval(intervalHandle);
            intervalHandle = null;
        }
    }

    const shiftVideoToIndex = (index) => {
        const myPlayer = player || currentPlayer;
        //the YouTube API only attaches the player methods once the player is ready
        if(myPlayer && myPlayer.getPlaylistIndex && myPlayer.getPlaylistIndex() !== index) {
            myPlayer.playVideoAt(index);
            const videoId = Object.keys(service.VideoDurations)[index];
            setCurrentVideoID(videoId);
        }
    }

    const setCurrentTimeInterval = () => {
        if(!intervalHandle && service) {
            intervalHandle = setInterval(() => {
                const myPlayer = player || currentPlayer;
                if(myPlayer) {
                    let playedSecs = 0;
                    const videoIDs = Object.keys(service.VideoDurations);
                    for(let i = 0; i < myPlayer.getPlaylistIndex(); i++) {
                        const videoID = videoIDs[i];
                        playedSecs += service.VideoDurations[videoID].Duration;
                    }
                    const currentVideoID = myPlayer.getVideoData()?.video_id;
                    const currentVideoDuration = service.VideoDurations[currentVideoID];
                    if(currentVideoDuration) {
                        setVideoStartTime(formatDuration(currentVideoDuration.CropSecsStart, false));
                        setVideoEndTime(formatDuration(myPlayer.getDuration() - currentVideoDuration.CropSecs, false));
                        //ignore the time before the crop start
                        const currentTime = myPlayer.getCurrentTime();
                        playedSecs += currentTime < currentVideoDuration.CropSecsStart ? 0 : currentTime - currentVideoDuration.CropSecsStart;
                        setCurrentTime(formatDuration(playedSecs));
                    }
                }
            }, 500);
        }
    }

    const getCurrentTimeSecs = () => {
        const myPlayer = player || currentPlayer;
        if(myPlayer && myPlayer.getVideoData) {
            const currentVideoID = myPlayer.getVideoData()?.video_id;
            const currentVideoDuration = service.VideoDurations[currentVideoID];
            //no video is loaded yet when the player first becomes ready
            if(currentVideoDuration) {
                //ignore the time before the crop start
                const currentTime = myPlayer.getCurrentTime();
                return currentTime < currentVideoDuration.CropSecsStart ? 0 : (Math.round(currentTime * 10) / 10) - currentVideoDuration.CropSecsStart;
            }
        }
    }

    const videoInterpretationLanguages = Object.keys(videoInterpretation?.Interpretations || {});

    const onAudioPlay = (event) => {
        const audio = event.currentTarget;
        const myPlayer = player || currentPlayer;
        if(myPlayer && myPlayer.getVideoData && (myPlayer.getPlayerState() === window.YT.PlayerState.PLAYING || myPlayer.getPlayerState() === window.YT.PlayerState.BUFFERING)) {
            audio.currentTime = myPlayer.getCurrentTime();
        }
    } 

    return (
        service ?  
            <div style={isAdmin ? {display: 'flex', flexFlow: 'row wrap'} : null}>
                <Card key={service.ServiceID} style={{minWidth: '18rem', marginBottom: '30px', maxWidth: isAdmin ? '50%' : '100%'}}>
                    <Card.Header>
                        <Card.Title>{service.ServiceTitle}</Card.Title>
                    </Card.Header>
                    <Card.Body style={{textAlign: 'left'}}>     
                        <Card.Text>
                            Location: {service.Location}
                        </Card.Text>           
                        <Card.Text>
                            Date: {getStartDateText(service)}  
                        </Card.Text>
                        <Card.Text>
                            Start Time: {getStartTimeText(service)}
                        </Card.Text>
                        <Card.Text>
                            End Time: {getEndTimeText(service)}
                        </Card.Text>
                        <Card.Text>
                            Watch window closed: {formatTime(service.EndTime)}
                            <br />
                            <small className="text-muted">Viewers who started the service late have until this time to finish watching</small>
                        </Card.Text>
                        <Card.Text>
                            Programme:
                        </Card.Text>
                        <ol>
                        {
                            getProgramme().map((item, index) =>
                                <li key={item.VideoID} onClick={isAdmin && playerReady ? () => shiftVideoToIndex(index) : null} style={{textDecoration: isAdmin && playerReady ? 'underline': 'none', cursor: isAdmin && playerReady ? 'pointer': 'default'}}>{item.StartTime} - {item.EndTime}: {item.Title}</li>
                            )
                        }
                        </ol>
                    </Card.Body>
                    <Card.Footer>
                        <Link to={'/'}>
                            Back
                        </Link>
                    </Card.Footer>
                </Card> 
                {
                    isAdmin ?
                        <div style={{ width: '50%', padding: '0 15px', maxHeight: '500px' }}>
                            <div>Preview Service</div>
                            <div>Current Time: {currentTime}</div>
                            <div>Video Start: {videoStartTime || '-'}, Video End: {videoEndTime || '-'}</div>
                            {playerError && <div style={{color: 'red'}}>Player error {playerError}</div>}
                            <iframe id="contentFrame" width="100%" height="100%" src={`//www.youtube.com/embed/playlist?autoplay=0&controls=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&iv_load_policy=3&cc_load_policy=1&cc_lang_pref=pl&list=${playlistID}&origin=${window.location.origin}&index=0&start=0`} frameBorder="0" allow="accelerometer; encrypted-media; gyroscope" allowFullScreen></iframe>
                            <div>{getCurrentTimeSecs()} secs</div>
                            {
                                !!videoInterpretationLanguages.length && <div>
                                    Interpretation:
                                    {
                                        videoInterpretationLanguages.map(language =>
                                        <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center'}}>
                                            <span style={{margin: 'auto 20px'}}>{language}: </span>
                                            <audio key={language} onPlay={onAudioPlay} controls src={videoInterpretation.Interpretations[language].AudioPath} />
                                        </div>)
                                    }
                                </div>
                            }
                        </div>
                        : null  
                }
                
            </div>
            : 
            loadingServiceData ? 'Loading...' : null
    );
}

export default ServiceDetailsPage;
